import React, { useState, useEffect, useCallback, useRef } from 'react';
import ModalLoading from './ModalLoading';
import { getPresenceStatusText, getSocialPresenceState } from './socialPresencePresentation';
import { getAvatarVariantUrl } from '../utils/avatarVariants';
import ProgressPortrait from './ProgressPortrait';
import ProgressImagePanel from './ProgressImagePanel';

/**
 * Modal to display a friend's or dojo member's profile with badge and stats
 */
const SocialProfileModal = ({ 
  userId, 
  userName,
  close, 
  backendService,
}) => {
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStatsLoading, setIsStatsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const statsRequestRef = useRef(0);
  const statsAbortRef = useRef(null);

  const loadStats = useCallback(async () => {
    const requestId = ++statsRequestRef.current;
    statsAbortRef.current?.abort();
    const controller = new AbortController();
    statsAbortRef.current = controller;
    setIsStatsLoading(true);
    setStatsError(null);
    try {
      const data = await backendService.getSocialProfileStats(userId, { signal: controller.signal });
      if (requestId === statsRequestRef.current) setStats(data);
    } catch (err) {
      const cancelled = err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError';
      if (!cancelled && requestId === statsRequestRef.current) {
        console.error('Error fetching social profile stats:', err);
        setStatsError('Stats unavailable');
      }
    } finally {
      if (requestId === statsRequestRef.current) {
        setIsStatsLoading(false);
        if (statsAbortRef.current === controller) statsAbortRef.current = null;
      }
    }
  }, [backendService, userId]);

  useEffect(() => {
    let active = true;
    const fetchProfile = async () => {
      setIsLoading(true);
      setProfile(null);
      setStats(null);
      setError(null);
      setStatsError(null);
      try {
        const data = await backendService.getSocialProfile(userId);
        if (!active) return;
        setProfile(data);
        setIsLoading(false);
        loadStats();
      } catch (err) {
        if (!active) return;
        console.error('Error fetching social profile:', err);
        if (err.response?.status === 403) {
          setError('You do not have permission to view this profile');
        } else if (err.response?.status === 404) {
          setError('Profile not found');
        } else {
          setError('Could not load profile');
        }
        setIsLoading(false);
      }
    };

    fetchProfile();
    return () => {
      active = false;
      statsAbortRef.current?.abort();
      statsAbortRef.current = null;
      statsRequestRef.current += 1;
    };
  }, [userId, backendService, loadStats]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateStr);
  };

  const getActivityTypeEmoji = (type) => {
    switch (type) {
      case 'workout': return '🏋️';
      case 'meal': return '🍽️';
      case 'media': return '📸';
      case 'metric': return '📊';
      case 'survey': return '📝';
      default: return '📌';
    }
  };

  const getRelationshipLabel = (relationship) => {
    switch (relationship) {
      case 'self': return '⭐ Your Profile';
      case 'friend': return '👤 Friend';
      case 'dojo_member': return '🥋 Dojo Member';
      default: return '';
    }
  };

  const isSelf = profile?.relationship === 'self';
  const presence = getSocialPresenceState(profile?.status);
  const presenceStatusText = getPresenceStatusText(profile?.status, formatRelativeTime);
  const portraitProgress = stats ? stats.progressPortrait?.week : profile?.progressPortrait?.week;
  const portraitVisible = (stats?.progressPortrait?.visible ?? profile?.progressPortrait?.visible) === true;
  const portraitState = isStatsLoading || (!stats && !statsError)
    ? 'loading'
    : statsError || !stats ? 'unavailable' : 'ready';

  return (
    <div className={`social-profile-modal ${isSelf ? 'is-self' : ''}`}>
      {isLoading ? (
        <ModalLoading text="Loading profile..." />
      ) : error ? (
        <div className="social-profile-error">
          <p>⚠️ {error}</p>
        </div>
      ) : profile ? (
        <div className="social-profile-content">
            {/* Profile identity is intentionally stacked: the avatar is the focal point,
                while progress/status support the identity instead of competing with it. */}
            <div className={`social-profile-avatar-row presence-${presence.key}`}>
              <ProgressPortrait
                user={{ ...profile.user, avatar_url: getAvatarVariantUrl(profile.user, 'profile') }}
                progressPercent={portraitProgress}
                monthPercent={portraitVisible && portraitState === 'ready' ? stats?.complianceStats?.month ?? null : null}
                yearPercent={portraitVisible && portraitState === 'ready' ? stats?.complianceStats?.year ?? null : null}
                progressState={portraitState}
                visible={portraitVisible}
                size="profile"
                status={presence.key}
              />
            </div>
            <div className="social-profile-identity">
              <h3>{isSelf ? 'You' : (profile.user.display_name || profile.user.email?.split('@')[0])}</h3>
              {profile.status && (
                <div className={`social-profile-identity-status presence-${presence.key}`}>
                  <span className="online-indicator"></span>
                  <span className="status-text">{presenceStatusText}</span>
                </div>
              )}
              {presence.workoutName && (
                <div className="social-profile-workout-context">{presence.workoutName} · In progress</div>
              )}
            </div>
            <div className="social-profile-details">
              <p className="social-profile-relationship">{getRelationshipLabel(profile.relationship)}</p>
              {profile.dojo?.name && (
                <p className="social-profile-dojo">🥋 {profile.dojo.name}{profile.dojo.role === 'chief' ? ' · Chief' : ''}</p>
              )}
              <p className="social-profile-member-since">Member since {formatDate(profile.user.member_since)}</p>
            </div>

            <ProgressImagePanel backendService={backendService} userId={userId} />

            {/* Stats Breakdown */}
            <div className="social-profile-stats">
              <h4>Stats</h4>
              {isStatsLoading && (
                <div className="social-profile-stats-loading" role="status" aria-live="polite">
                  <span className="social-profile-stats-spinner" aria-hidden="true" />
                  <span>Loading stats…</span>
                </div>
              )}
              {!isStatsLoading && statsError && (
                <div className="social-profile-stats-error" role="status">
                  <span>{statsError}</span>
                  <button type="button" onClick={loadStats}>Retry</button>
                </div>
              )}
              {!isStatsLoading && !statsError && stats?.errors?.length > 0 && (
                <div className="social-profile-stats-partial" role="status">Some stats unavailable</div>
              )}
              {/* Plan Compliance - First like in compliance modal */}
              {stats?.complianceStats && (stats?.complianceStats.week !== null || stats?.complianceStats.month !== null || stats?.complianceStats.year !== null) && (
                <div className="social-profile-stat-group">
                  <h5>📋 Plan Compliance</h5>
                  {stats?.complianceStats.week !== null && (
                    <div className="social-profile-stat-row">
                      <span>Week:</span>
                      <span className="stat-value">{Math.round(stats?.complianceStats.week)}%</span>
                    </div>
                  )}
                  {stats?.complianceStats.month !== null && (
                    <div className="social-profile-stat-row">
                      <span>Month:</span>
                      <span className="stat-value">{Math.round(stats?.complianceStats.month)}%</span>
                    </div>
                  )}
                  {stats?.complianceStats.year !== null && (
                    <div className="social-profile-stat-row">
                      <span>Year:</span>
                      <span className="stat-value">{Math.round(stats?.complianceStats.year)}%</span>
                    </div>
                  )}
                </div>
              )}

              {/* Volume PRs */}
              {stats?.badgeStats?.volumePRs && (
                <div className="social-profile-stat-group">
                  <h5>🏋️ Volume PRs</h5>
                  <div className="social-profile-stat-row">
                    <span>Week:</span>
                    <span className="stat-value">{stats?.badgeStats.volumePRs.week?.prRate || 0}%</span>
                  </div>
                  <div className="social-profile-stat-row">
                    <span>Month:</span>
                    <span className="stat-value">{stats?.badgeStats.volumePRs.month?.prRate || 0}%</span>
                  </div>
                  <div className="social-profile-stat-row">
                    <span>Year:</span>
                    <span className="stat-value">{stats?.badgeStats.volumePRs.year?.prRate || 0}%</span>
                  </div>
                </div>
              )}

              {/* Nutrition Compliance */}
              {stats?.badgeStats?.nutritionCompliance && (
                <div className="social-profile-stat-group">
                  <h5>🥗 Nutrition Compliance</h5>
                  <div className="social-profile-stat-row">
                    <span>Week:</span>
                    <span className="stat-value">{stats?.badgeStats.nutritionCompliance.week?.complianceRate || 0}%</span>
                  </div>
                  <div className="social-profile-stat-row">
                    <span>Month:</span>
                    <span className="stat-value">{stats?.badgeStats.nutritionCompliance.month?.complianceRate || 0}%</span>
                  </div>
                  <div className="social-profile-stat-row">
                    <span>Year:</span>
                    <span className="stat-value">{stats?.badgeStats.nutritionCompliance.year?.complianceRate || 0}%</span>
                  </div>
                </div>
              )}

              {/* Recent Activity Summary */}
              {stats?.activitySummary && Object.keys(stats?.activitySummary).length > 0 && (
                <div className="social-profile-stat-group">
                  <h5>📊 Last 7 Days</h5>
                  {stats?.activitySummary.workout && (
                    <div className="social-profile-stat-row">
                      <span>🏋️ Workouts:</span>
                      <span className="stat-value">{stats?.activitySummary.workout}</span>
                    </div>
                  )}
                  {stats?.activitySummary.meal && (
                    <div className="social-profile-stat-row">
                      <span>🍽️ Meals:</span>
                      <span className="stat-value">{stats?.activitySummary.meal}</span>
                    </div>
                  )}
                  {stats?.activitySummary.media && (
                    <div className="social-profile-stat-row">
                      <span>📸 Media:</span>
                      <span className="stat-value">{stats?.activitySummary.media}</span>
                    </div>
                  )}
                  {stats?.activitySummary.metric && (
                    <div className="social-profile-stat-row">
                      <span>📊 Metrics:</span>
                      <span className="stat-value">{stats?.activitySummary.metric}</span>
                    </div>
                  )}
                  {stats?.activitySummary.survey && (
                    <div className="social-profile-stat-row">
                      <span>📝 Surveys:</span>
                      <span className="stat-value">{stats?.activitySummary.survey}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Last Activity by Type */}
              {stats?.lastActivityByType && Object.keys(stats.lastActivityByType).length > 0 && (
                <div className="social-profile-stat-group">
                  <h5>🕐 Last Activity</h5>
                  {Object.entries(stats.lastActivityByType).map(([type, data]) => (
                    <div key={type} className="social-profile-stat-row">
                      <span>{getActivityTypeEmoji(type)} {type.charAt(0).toUpperCase() + type.slice(1)}:</span>
                      <span className="stat-value stat-value-time">{formatRelativeTime(data.completedAt)}</span>
                    </div>
                  ))}
                </div>
              )}

            </div>
          </div>
        ) : null}


    </div>
  );
};

export default SocialProfileModal;
